import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaArrowLeft as FaArrowLeftRaw,
  FaBook as FaBookRaw,
  FaClipboardList as FaClipboardListRaw,
  FaPlus as FaPlusRaw,
  FaUserFriends as FaUserFriendsRaw,
} from 'react-icons/fa';
import { teacherService, type TeacherWorkItem } from '../../services/classroom/teacherService';
import ClassSectionSidebar from '../../components/classroom/ClassSectionSidebar';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaArrowLeft: any = FaArrowLeftRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBook: any = FaBookRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClipboardList: any = FaClipboardListRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUserFriends: any = FaUserFriendsRaw;

const MOBILE_BREAKPOINT = '1024px';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const BackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Hero = styled.section<{ $imageUrl?: string | null }>`
  border-radius: 16px;
  border: 1px solid var(--outline);
  padding: 1.5rem 1.65rem 1.35rem;
  color: var(--onPrimary);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.1);
  background: ${({ $imageUrl }) => {
    const base = 'linear-gradient(135deg, var(--primaryDark) 0%, var(--primary) 55%, color-mix(in srgb, var(--primary) 70%, var(--surface)) 100%)';
    const url = typeof $imageUrl === 'string' ? $imageUrl.trim() : '';
    if (!url) return base;
    return `linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.15) 100%), url(${url}) center / cover no-repeat, ${base}`;
  }};
`;

const Breadcrumb = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.88;
`;

const HeroTitle = styled.h1`
  margin: 0 0 1rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.45rem, 3vw, 2rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.02em;
`;

const HeroStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const HeroPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(6px);
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const MainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const Empty = styled.p`
  margin: 0;
  padding: 1.25rem;
  border-radius: 14px;
  border: 1px dashed var(--outline);
  background: var(--surfaceVariant);
  font-size: 0.9rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const TopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
`;

const CreateBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.48rem 0.9rem;
  border-radius: 10px;
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.82rem;
  font-weight: 700;
  text-decoration: none;
  &:hover {
    background: var(--primaryDark);
  }
`;

const WorkCard = styled.article`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  padding: 0.85rem 1rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const WorkTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
`;

const WorkTitle = styled.h3`
  margin: 0;
  font-size: 0.96rem;
  font-weight: 700;
  color: var(--text);
`;

const TypePill = styled.span<{ $type: 'quiz' | 'assignment' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  ${({ $type }) =>
    $type === 'quiz'
      ? `
    background: color-mix(in srgb, var(--primary) 14%, transparent);
    color: var(--primary);
  `
      : `
    background: color-mix(in srgb, var(--warning, #e8a23c) 18%, transparent);
    color: var(--warning, #b45309);
  `}
`;

const MetaRow = styled.div`
  margin-top: 0.45rem;
  font-size: 0.82rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const ActionLink = styled(Link)`
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

function classCodeLabel(id: string, className: string): string {
  const short = id.replace(/-/g, '').slice(0, 6).toUpperCase();
  const topic = className.split(/\s+/).slice(0, 2).join(' ').toUpperCase();
  return `${topic || 'CLASS'} · ${short || 'NEW'}`;
}

function dueLabel(iso: string | null): string {
  if (!iso) return 'Draft';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'Due soon';
  return when.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function sortWork(a: TeacherWorkItem, b: TeacherWorkItem): number {
  if (Boolean(a.archived) !== Boolean(b.archived)) return a.archived ? 1 : -1;
  const aDue = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
  const bDue = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
  if (aDue !== bDue) return aDue - bDue;
  return a.title.localeCompare(b.title);
}

const ClassroomTeacherClassAssignmentsPage: React.FC = () => {
  const { classId = '' } = useParams<{ classId: string }>();
  const [overviewQuery, assignmentsQuery, quizzesQuery] = useQueries({
    queries: [
      { queryKey: ['classroom', 'teacher', 'overview'], queryFn: teacherService.getOverview },
      { queryKey: ['classroom', 'teacher', 'work', 'assignment'], queryFn: () => teacherService.getWork('assignment') },
      { queryKey: ['classroom', 'teacher', 'work', 'quiz'], queryFn: () => teacherService.getWork('quiz') },
    ],
  });

  if (overviewQuery.isLoading || assignmentsQuery.isLoading || quizzesQuery.isLoading) {
    return <Empty>Loading class assignments…</Empty>;
  }
  if (overviewQuery.isError || assignmentsQuery.isError || quizzesQuery.isError) {
    return <Empty>Unable to load class assignments.</Empty>;
  }

  const classItem = (overviewQuery.data ?? []).find((row) => row.id === classId);
  if (!classItem) {
    return (
      <Page>
        <BackRow>
          <BackLink to="/classroom-teacher/classes">
            <FaArrowLeft size={12} aria-hidden />
            Back to Classes
          </BackLink>
        </BackRow>
        <Empty>Class not found.</Empty>
      </Page>
    );
  }

  const classAssignments = (assignmentsQuery.data ?? []).filter((row) => row.class_id === classId);
  const classQuizzes = (quizzesQuery.data ?? []).filter((row) => row.class_id === classId);
  const workRows = [...classAssignments, ...classQuizzes].sort(sortWork);
  const classLessonsCount = classItem.upcomingAssignments;
  const shortClassName = classItem.name.split(/\s+/).slice(0, 3).join(' ') || 'This class';

  return (
    <Page>
      <BackRow>
        <BackLink to="/classroom-teacher/classes">
          <FaArrowLeft size={12} aria-hidden />
          Back to Classes
        </BackLink>
      </BackRow>

      <Hero $imageUrl={classItem.class_image_url}>
        <Breadcrumb>{classCodeLabel(classId, classItem.name)}</Breadcrumb>
        <HeroTitle>Assignments</HeroTitle>
        <HeroStats>
          <HeroPill>
            <FaUserFriends size={14} aria-hidden />
            {classItem.activeStudents} Students
          </HeroPill>
          <HeroPill>
            <FaBook size={14} aria-hidden />
            {classLessonsCount} Upcoming
          </HeroPill>
          <HeroPill>
            <FaClipboardList size={14} aria-hidden />
            {workRows.length} Total items
          </HeroPill>
        </HeroStats>
      </Hero>

      <LayoutGrid>
        <ClassSectionSidebar classId={classId} className={shortClassName} />

        <MainCol>
          <TopRow>
            <SectionTitle>Class assignments and quizzes</SectionTitle>
            <CreateBtn to="/classroom-teacher/assignments/new">
              <FaPlus size={12} aria-hidden />
              Create assignment
            </CreateBtn>
          </TopRow>

          {workRows.length === 0 ? (
            <Empty>No assignments or quizzes yet for this class.</Empty>
          ) : (
            workRows.map((row) => (
              <WorkCard key={`${row.type}-${row.id}`}>
                <WorkTop>
                  <div>
                    <TypePill $type={row.type}>{row.type === 'quiz' ? 'QUIZ' : 'ASSIGNMENT'}</TypePill>
                    <WorkTitle>{row.title}</WorkTitle>
                  </div>
                  <ActionLink
                    to={
                      row.type === 'quiz'
                        ? `/classroom-teacher/quizzes/${encodeURIComponent(row.id)}/edit`
                        : `/classroom-teacher/assignments/${encodeURIComponent(row.id)}`
                    }
                  >
                    {row.type === 'quiz' ? 'View quiz' : 'View assignment'}
                  </ActionLink>
                </WorkTop>
                <MetaRow>
                  {row.points} pts · {dueLabel(row.due_at)}
                  {row.archived ? ' · Archived' : ''}
                </MetaRow>
              </WorkCard>
            ))
          )}
        </MainCol>
      </LayoutGrid>
    </Page>
  );
};

export default ClassroomTeacherClassAssignmentsPage;
